import { useEffect, useState } from "react";
import { Receipt, Plus, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useFideicomiso } from "../lib/fideicomiso";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const ESTADO_BADGE = {
    pendiente: "badge-gold",
    presentada: "badge-exito",
    vencida: "badge-error",
    en_proceso: "badge-gold",
    no_aplica: "badge-mock",
};

export default function Fiscal() {
    const { selected } = useFideicomiso();
    const [tab, setTab] = useState("config");
    const [cfg, setCfg] = useState(null);
    const [oblig, setOblig] = useState([]);
    const [catalogos, setCatalogos] = useState({ tipos_obligacion: [], clasificaciones: [] });
    const [openCfg, setOpenCfg] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        clasificacion: "empresarial", rfc_fideicomiso: "", regimen: "601",
        retenedor: false, obligaciones_activas: [],
    });

    const load = async () => {
        if (!selected) return;
        const [{ data: c }, { data: o }, { data: cat }] = await Promise.all([
            api.get(`/fideicomisos/${selected.id}/configuracion-fiscal`),
            api.get(`/obligaciones-fiscales?fideicomiso_id=${selected.id}`),
            api.get("/fiscal/catalogos"),
        ]);
        setCfg(c); setOblig(o); setCatalogos(cat);
        if (c?.clasificacion) {
            setForm({
                clasificacion: c.clasificacion,
                rfc_fideicomiso: c.rfc_fideicomiso || "",
                regimen: c.regimen || "601",
                retenedor: c.retenedor || false,
                obligaciones_activas: c.obligaciones_activas || [],
            });
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected?.id]);

    const addOblig = () => setForm((f) => ({
        ...f, obligaciones_activas: [...f.obligaciones_activas,
            { tipo: "iva_mensual", periodicidad: "mensual", fecha_limite_dia: 17, activo: true }],
    }));
    const rmOblig = (idx) => setForm((f) => ({
        ...f, obligaciones_activas: f.obligaciones_activas.filter((_, i) => i !== idx),
    }));
    const setOblig_ = (idx, k, v) => setForm((f) => ({
        ...f, obligaciones_activas: f.obligaciones_activas.map((o, i) => i === idx ? { ...o, [k]: v } : o),
    }));

    const submit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.put(`/fideicomisos/${selected.id}/configuracion-fiscal`, form);
            toast.success("Configuración fiscal guardada");
            setOpenCfg(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const generarMes = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/obligaciones-fiscales/generar-mes?fideicomiso_id=${selected.id}`);
            toast.success(`Generadas: ${data.creados}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const presentar = async (id) => {
        try {
            await api.post(`/obligaciones-fiscales/${id}/presentar`, {});
            toast.success("Marcada como presentada");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    };

    if (!selected) return <div className="p-10 text-muted-foreground text-sm">Selecciona un fideicomiso.</div>;

    return (
        <div className="p-8 lg:p-10 max-w-[1400px] space-y-6" data-testid="fiscal-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Núcleo · {selected.numero_interno}
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <Receipt className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Fiscal
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        <span className="badge-mock">MOCK</span> Calendario de obligaciones · sin cálculo de impuestos
                    </p>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="config">Configuración</TabsTrigger>
                    <TabsTrigger value="calendario">Calendario de obligaciones</TabsTrigger>
                </TabsList>

                <TabsContent value="config">
                    <div className="kpi-card" data-testid="configuracion-fiscal-form">
                        {cfg?.clasificacion ? (
                            <div className="grid md:grid-cols-4 gap-3">
                                <div><div className="text-[10px] uppercase text-muted-foreground">Clasificación</div>
                                    <div className="text-sm font-medium">{cfg.clasificacion}</div></div>
                                <div><div className="text-[10px] uppercase text-muted-foreground">RFC fideicomiso</div>
                                    <div className="text-sm font-medium tabular">{cfg.rfc_fideicomiso || "—"}</div></div>
                                <div><div className="text-[10px] uppercase text-muted-foreground">Régimen</div>
                                    <div className="text-sm font-medium">{cfg.regimen || "—"}</div></div>
                                <div><div className="text-[10px] uppercase text-muted-foreground">Retenedor</div>
                                    <div className="text-sm font-medium">{cfg.retenedor ? "Sí" : "No"}</div></div>
                                <div className="col-span-full">
                                    <div className="text-[10px] uppercase text-muted-foreground mb-1">Obligaciones activas</div>
                                    <div className="flex flex-wrap gap-1">
                                        {(cfg.obligaciones_activas || []).filter((o) => o.activo).map((o) => (
                                            <span key={o.tipo} className="badge-gold">
                                                {o.tipo} · {o.periodicidad}
                                            </span>
                                        ))}
                                        {(cfg.obligaciones_activas || []).length === 0 && (
                                            <span className="text-xs text-muted-foreground">Sin obligaciones activas.</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm text-muted-foreground">Sin configuración fiscal.</div>
                        )}
                        <div className="mt-4 flex gap-2">
                            <Dialog open={openCfg} onOpenChange={setOpenCfg}>
                                <DialogTrigger asChild>
                                    <Button size="sm" data-testid="editar-config-fiscal-btn">
                                        {cfg?.clasificacion ? "Editar" : "Configurar"}
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl">
                                    <DialogHeader><DialogTitle>Configuración fiscal</DialogTitle></DialogHeader>
                                    <form onSubmit={submit} className="space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div><Label>Clasificación</Label>
                                                <select className="w-full border border-border rounded-sm px-2 py-1.5 bg-background text-sm"
                                                    value={form.clasificacion}
                                                    onChange={(e) => setForm({ ...form, clasificacion: e.target.value })}>
                                                    {catalogos.clasificaciones.map((c) => <option key={c}>{c}</option>)}
                                                </select>
                                            </div>
                                            <div><Label>RFC fideicomiso</Label>
                                                <Input value={form.rfc_fideicomiso}
                                                    onChange={(e) => setForm({ ...form, rfc_fideicomiso: e.target.value })} />
                                            </div>
                                            <div><Label>Régimen</Label>
                                                <Input value={form.regimen}
                                                    onChange={(e) => setForm({ ...form, regimen: e.target.value })} />
                                            </div>
                                            <div className="flex items-end gap-2">
                                                <label className="flex items-center gap-2 text-sm">
                                                    <input type="checkbox" checked={form.retenedor}
                                                        onChange={(e) => setForm({ ...form, retenedor: e.target.checked })} />
                                                    Retenedor ISR
                                                </label>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <Label>Obligaciones activas</Label>
                                                <Button type="button" size="sm" variant="ghost" onClick={addOblig}>
                                                    <Plus className="h-3 w-3 mr-1" /> Añadir
                                                </Button>
                                            </div>
                                            {form.obligaciones_activas.map((o, idx) => (
                                                <div key={idx} className="grid grid-cols-5 gap-2 mb-1 items-center">
                                                    <select className="col-span-2 border border-border rounded-sm px-1 py-1 bg-background text-xs"
                                                        value={o.tipo}
                                                        onChange={(e) => setOblig_(idx, "tipo", e.target.value)}>
                                                        {catalogos.tipos_obligacion.map((t) => <option key={t}>{t}</option>)}
                                                    </select>
                                                    <select className="border border-border rounded-sm px-1 py-1 bg-background text-xs"
                                                        value={o.periodicidad}
                                                        onChange={(e) => setOblig_(idx, "periodicidad", e.target.value)}>
                                                        <option value="mensual">mensual</option>
                                                        <option value="trimestral">trimestral</option>
                                                        <option value="anual">anual</option>
                                                    </select>
                                                    <Input type="number" min="1" max="31" className="text-xs"
                                                        value={o.fecha_limite_dia}
                                                        onChange={(e) => setOblig_(idx, "fecha_limite_dia", parseInt(e.target.value, 10))} />
                                                    <Button type="button" size="sm" variant="ghost"
                                                        onClick={() => rmOblig(idx)}>×</Button>
                                                </div>
                                            ))}
                                        </div>
                                        <DialogFooter>
                                            <Button type="submit" disabled={busy}>
                                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
                                            </Button>
                                        </DialogFooter>
                                    </form>
                                </DialogContent>
                            </Dialog>
                            <Button size="sm" variant="outline" onClick={generarMes} disabled={busy}
                                data-testid="nueva-obligacion-fiscal-btn">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> :
                                    "Generar obligaciones del mes"}
                            </Button>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="calendario">
                    <div className="rounded-md border border-border bg-card overflow-hidden">
                        <table className="trf-table">
                            <thead><tr>
                                <th>Tipo</th><th>Periodo</th><th>Fecha límite</th>
                                <th>Periodicidad</th><th>Estado</th><th></th>
                            </tr></thead>
                            <tbody>
                                {oblig.map((o) => (
                                    <tr key={o.id} data-testid={`obligacion-fiscal-${o.id}`}>
                                        <td className="font-medium text-xs">{o.tipo}</td>
                                        <td className="tabular">{o.periodo}</td>
                                        <td className="tabular">{o.fecha_limite}</td>
                                        <td className="text-xs">{o.periodicidad}</td>
                                        <td><span className={ESTADO_BADGE[o.estado] || "badge-gold"}>{o.estado}</span></td>
                                        <td>
                                            {o.estado === "pendiente" && (
                                                <Button size="sm" variant="ghost"
                                                    onClick={() => presentar(o.id)}
                                                    data-testid={`presentar-obligacion-${o.id}`}>
                                                    <CheckCircle2 className="h-3 w-3 mr-1" /> Presentar
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {oblig.length === 0 && (
                                    <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">Sin obligaciones registradas.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
